import {isRecoverableOfferCheckError} from 'modules/accommodation/offerCheckError'
import {
  setOfferCheckRetryableError,
  setShouldDisplayAlternativeOffers,
  setSoldoutOfferById
} from 'modules/accommodation/slice'
import {getFilteredRoomsData} from 'modules/searchApi/selectors'

import {
  getOnAuthSuccessLandingUrl,
  removeOnAuthSuccessLandingUrl
} from '@vio/core/src/auth/business/persistData'
import {
  TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY,
  userSuccessfullyAuthenticated,
  userSuccessfullyAuthenticatedWithEmail
} from '@vio/core/src/auth/modules/actions'
import {
  cleanUpLoginErrors,
  hideBottomSheetLogin,
  hidePasswordlessLogin,
  resetSusiState
} from '@vio/core/src/auth/modules/slice'
import {offerCheckApi} from '@vio/core/src/offer/services/offerCheckApi'
import {removeSusiQueryParam} from '@vio/shared/src/authentication/hooks/useSUSIBottomSheet'
import {dealFreezeApi} from '@vio/shared/src/dealFreeze/services/dealFreezeApi'

import {MiddlewareType} from '../middlewareList'

const accommodationMiddleware: MiddlewareType = store => next => action => {
  if (
    [
      userSuccessfullyAuthenticated.type,
      userSuccessfullyAuthenticatedWithEmail.type
    ].includes(action.type)
  ) {
    const successLandingUrl = getOnAuthSuccessLandingUrl()
    if (successLandingUrl) {
      const {dispatch} = store
      // Close overlay/bottom sheet so the bf-cache snapshot is clean
      dispatch(cleanUpLoginErrors())
      dispatch(hidePasswordlessLogin())
      dispatch(hideBottomSheetLogin())
      dispatch(resetSusiState())
      removeSusiQueryParam()

      setTimeout(() => {
        removeOnAuthSuccessLandingUrl()
        window.location.href = successLandingUrl
      }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)
      return
    }
  }

  if (offerCheckApi.endpoints.getOfferCheck.matchRejected(action)) {
    const {dispatch, getState} = store
    const {offerId, isProactiveCheck} = action.meta.arg.originalArgs.options

    // Transport, server, or unexpected failures (anything but a confirmed BoVio
    // sold-out) must not render as sold-out. Surface a retryable toast for
    // user-initiated checks and keep the offer bookable so the user can retry.
    if (isRecoverableOfferCheckError(action.payload)) {
      if (!isProactiveCheck) {
        dispatch(setOfferCheckRetryableError(offerId))
      }
      return next(action)
    }

    const state = getState()
    const {rooms, splitBooking} = getFilteredRoomsData(state)
    dispatch(setSoldoutOfferById(offerId))

    const roomOfferIds = rooms.flatMap(room =>
      room.offers.map(offer => offer.id)
    )

    const soldOutOfferIds = {
      ...state.accommodation.soldoutOfferIds,
      [offerId]: true
    }

    const bundleId = splitBooking?.bundleId
    const allRoomOfferIds = [...roomOfferIds, ...(bundleId ? [bundleId] : [])]

    const isAllSoldOut =
      allRoomOfferIds.length > 0 &&
      allRoomOfferIds.every(id => soldOutOfferIds[id])

    if (isAllSoldOut) {
      dispatch(setShouldDisplayAlternativeOffers(true))
    }
  }

  // If offer check comes back as sold out in deal freeze, mark it as sold out in accommodation slice
  if (dealFreezeApi.endpoints.offerCheck.matchRejected(action)) {
    const {dispatch, getState} = store
    const state = getState()
    const offerId = state.dealFreeze?.data?.selectedOffer?.id
    if (offerId) dispatch(setSoldoutOfferById(offerId))
  }
  return next(action)
}

export default accommodationMiddleware
